import { Controller } from "@hotwired/stimulus"
import { rethrowUnlessAbort } from "../utils/fetch-error.util"

// Connects to data-controller="navigate"
export default class extends Controller {
  // Monotonic across reconnects on purpose: resetting it in connect() would let
  // a still-pending request think it is the newest one again.
  #generation = 0
  #abortController = null

  disconnect() {
    this.#abortController?.abort()
    this.#abortController = null
  }

  async to(e) {
    e.preventDefault()
    const url = new URL(window.location.href)
    url.searchParams.set(e.target.dataset.param, e.target.value)
    url.searchParams.set("turbo", "true")

    if (e.target.dataset.additionalParams) {
      const additionalParams = JSON.parse(e.target.dataset.additionalParams)

      // Use arrow function to preserve `this` context
      additionalParams.forEach((param) => {
        const ids = Array.isArray(param.id) ? param.id : [param.id]
        const values = []

        ids.forEach((id) => {
          if (document.getElementById(id) || document.querySelector(`input[name="${id}"]`)) {
            values.push(this.#get_value_by_type(param.type, id))
          }
        })

        if (values.length > 0) {
          url.searchParams.set(param.param, values.join(","))
        }
      })
    }

    const disablesId = e.target.dataset.disables || null
    this.#setDisabled(disablesId, true)

    // A newer filter click supersedes the in-flight one: abort it so its
    // response can never overwrite the page, and bump the generation so the
    // superseded call also skips rendering and skips re-enabling the control.
    this.#abortController?.abort()
    const abortController = new AbortController()
    this.#abortController = abortController
    const generation = ++this.#generation

    try {
      const response = await fetch(url.toString(), {
        headers: { Accept: "text/vnd.turbo-stream.html, text/html;q=0.9" },
        signal: abortController.signal
      })
      const html = await response.text()
      const contentType = response.headers.get("content-type") || ""

      // A slower earlier response must never land after a newer one, and a
      // disconnected controller must not render into a page that moved on.
      if (generation !== this.#generation || !this.element.isConnected) return

      if (!response.ok || !contentType.includes("text/vnd.turbo-stream.html")) {
        console.error("[navigate] Expected turbo-stream response", {
          status: response.status,
          contentType,
          url: url.toString()
        })
        return
      }

      Turbo.renderStreamMessage(html)
    } catch (error) {
      rethrowUnlessAbort(error)
    } finally {
      if (this.#abortController === abortController) {
        this.#abortController = null
      }
      // Only the newest request owns the disabled control (in practice the
      // Save button). Re-enabling it from a superseded request would unlock the
      // UI while its replacement is still in flight.
      if (generation === this.#generation) {
        this.#setDisabled(disablesId, false)
      }
    }
  }

  // Resolved fresh each time: a turbo-stream render may have swapped the
  // element out from under an earlier reference.
  #setDisabled(id, disabled) {
    if (!id) return

    const element = document.getElementById(id)
    if (element) element.disabled = disabled
  }

  #get_value_by_type(type, id) {
    switch (type) {
      case "select":
      case "input":
        return document.getElementById(id)?.value || ""
      case "radio": {
        const checked = document.querySelector(`input[name="${id}"]:checked`)
        return checked ? checked.value : ""
      }
      default:
        return ""
    }
  }
}
